import {
	type MandatoryItemDataInvestmentTypeEnum,
	DistributionBinningModeEnum,
	type ParameterizedConceptDTO,
	type SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { Column, Controller, DebouncedSearchInput, Icon, Row } from "@mdotm/shadcn/legacy/components";
import { Switch } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { Select, Button, FormField } from "@mdotm/shadcn/mdotm/components";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import { useEffect, useMemo } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import z from "zod";
import Draggable from "$root/components/common/Draggable";
import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "$root/components/common/SidePanelV2";
import { StackContextBlock } from "$root/components/common/Stack";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { Divider } from "$root/components/Divider";
import { IconWalls } from "$root/components/IconWall";
import ReactQueryWrapper, { ReactQueriesWrapperBase } from "$root/components/ReactQueryWrapper";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { qualifier } from "$root/utils";
import { breakdownDimensionLabel, WorkspaceChartChain } from "../chain";
import {
	chartSeriesConcepts,
	chartTypeDrawsLegend,
	chartTypeSupportsThreshold,
	supportedChartTypes,
	workspaceChartTypeIcons,
	workspaceChartTypeLabels,
} from "../chart-type";
import { autoGenerateMetricName, DEFAULT_CHART_LABEL } from "../common";
import type { WorkspaceChartCtx } from "../ctx";
import {
	chartFormModel,
	conceptFormModel,
	conceptFormSubmitSchema,
	formFromConcept,
	keyMetricThresholdModel,
} from "../concept-form";
import { workspaceRepository } from "../repository";
import { isCustomAttributeConcept } from "../services/sharedConceptService";
import { selectConcept, useWorkspaceDispatch, useWorkspaceStore } from "../state";
import type { WorkspaceChartTypeEnum } from "../spec-gaps";
import { SidePanelListItem } from "../ui/SidePanelItem";

export function WorkspaceChartSettingsStack() {
	const stackCtx = useMandatoryContext(StackContextBlock);
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const { readState, slices } = useWorkspaceStore();
	const dispatch = useWorkspaceDispatch();
	const charts = readAtom(slices.charts$);
	const workspaceConceptsMap = workspaceRepository.getConceptMapDefinitions.useQuery();
	const workspaceTagConcepts = workspaceRepository.getTagConceptDefinitions.useQuery();

	if (charts.length === 0) {
		return (
			<>
				<SidePanelHeader
					title="Chart Settings"
					icon={{
						name: "Left",
						onClick() {
							stackCtx.pop();
						},
					}}
					description="Configure chart widgets and visualizations"
					primaryAction={{
						label: "Add chart",
						onClick() {
							sidePanelCtx.push(() => <WorkspaceCreateChartSettingsStack title="Add chart" />);
						},
					}}
				/>
				<Divider />
				<SidePanelContent className="flex-1">
					<IconWalls.WorkspaceMissingCharts
						onClick={() => sidePanelCtx.push(() => <WorkspaceCreateChartSettingsStack title="Add chart" />)}
					/>
				</SidePanelContent>
			</>
		);
	}

	return (
		<>
			<SidePanelHeader
				title="Chart Settings"
				icon={{
					name: "Left",
					onClick() {
						stackCtx.pop();
					},
				}}
				description="Configure chart widgets and visualizations"
				primaryAction={{
					label: "Add chart",
					onClick() {
						sidePanelCtx.push(() => <WorkspaceCreateChartSettingsStack title="Add chart" />);
					},
				}}
			/>
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<WithAtom atom$={slices.charts$}>
					{(chartSettings) => (
						<Controller value="">
							{(search) => {
								const searchLower = search.value.toLowerCase();
								return (
									<>
										<DebouncedSearchInput
											type="Body/M/Bold"
											className="mb-4"
											externalQuery=""
											placeholder="Search by keyword..."
											onChange={search.onChange}
										/>
										<div className="min-h-0 flex-1 flex flex-col overflow-y-auto">
											<ReactQueriesWrapperBase queries={[workspaceConceptsMap, workspaceTagConcepts]}>
												{([workspaceMap, availableTagsConcepts]) => (
													<Draggable
														items={chartSettings}
														onReorder={(charts) => void dispatch({ type: "reorderCharts", charts })}
														disabled={Boolean(search.value)}
														itemKey={(_, index) => `chart-id-${index}`}
													>
														{({ data: item, dragIcon, isDragging, index }) => {
															if (
																searchLower &&
																!`${item.label ?? ""} ${item.description ?? ""}`.toLowerCase().includes(searchLower)
															) {
																return null;
															}
															return (
																<Column className="py-1 overflow-hidden">
																	<Row gap={8} alignItems="center">
																		{dragIcon}
																		<SidePanelListItem
																			title={item.label ?? DEFAULT_CHART_LABEL}
																			description={item.description}
																			checked={!(item.preferences?.hide ?? false)}
																			onToggle={(visible) => {
																				const next = chartSettings.slice();
																				next[index] = {
																					...next[index],
																					preferences: {
																						...next[index].preferences,
																						hide: !visible,
																					},
																				};
																				void dispatch({
																					type: "reorderCharts",
																					charts: next,
																				});
																			}}
																			onDrillDown={() => {
																				// `chartHasSeries` and `conceptReferencesResolve` are enforced, so the chart has a first series
																				// and the pool holds it. What stood here was a toast saying "Something went wrong please
																				// re-try" and a silent `return null`, for a chart that cannot exist.
																				const parametrizedConcept =
																					selectConcept(readState(), item.series.at(0) ?? unreachable()) ??
																					unreachable();

																				sidePanelCtx.push(() => (
																					<WorkspaceEditChartSettingsStack
																						title={item.label ?? DEFAULT_CHART_LABEL}
																						index={index}
																						description={item.description}
																						initialData={formFromConcept(
																							parametrizedConcept,
																							workspaceMap.get(parametrizedConcept.conceptId),
																							{
																								chart: item,
																								tagConcepts: availableTagsConcepts,
																								resolveConcept: (uuid) => selectConcept(readState(), uuid),
																							},
																						)}
																					/>
																				));
																			}}
																		/>
																	</Row>
																	{!isDragging && <Divider className="border-dashed" />}
																</Column>
															);
														}}
													</Draggable>
												)}
											</ReactQueriesWrapperBase>
										</div>
									</>
								);
							}}
						</Controller>
					)}
				</WithAtom>
			</SidePanelContent>
		</>
	);
}

/**
 * The form model shared by create and edit: the concept's half plus the chart's, both declared where the
 * one translation that fills them lives (see {@link formFromConcept}).
 *
 * Deliberately flat rather than a discriminated union, and that is the constraint the whole arrangement is
 * built around: react-hook-form addresses fields by string path, so a union would make every
 * `form.watch("breakdownLimit")` a type error on the branches that lack it. The flat shape therefore *types*
 * the form and the discriminated union stays the *validation* — see {@link chartSettingsResolverSchema},
 * which is this same model with the union applied on top of it rather than a second list of fields.
 *
 * The dimension a stacked column breaks down by is **not** a field of its own: it is `breakdown` on the
 * chain, answered inside the exposure chain — {@link conceptFormModel} is where it comes from. Only the
 * display settings for the resulting categories are the chart's own.
 *
 * The discriminator is `chartType` and holds the persisted wire value, so submit writes it through
 * untouched. `HISTOGRAM` is the column chart there — see the doc on {@link WorkspaceChartTypeEnum}.
 */
const chartSettingsFormModel = conceptFormModel.merge(chartFormModel);

export type WorkspaceChartSettingsDetails = z.infer<typeof chartSettingsFormModel>;

/**
 * The key-metric threshold fields, spread into the branches of the chart types that
 * {@link chartTypeSupportsThreshold} accepts.
 *
 * The model already declares them for every kind — they are {@link keyMetricThresholdModel}, part of the
 * chart's half — so this is not what keeps them from being stripped. What restating them per branch buys is
 * the union *saying* which kinds own the setting, instead of every kind silently accepting one.
 */
const keyMetricThresholdSchemaShape = keyMetricThresholdModel.shape;

/**
 * What `chartLegend.visible` is persisted as.
 *
 * The chart type has the last word rather than only breaking a tie: a type that draws no legend must
 * persist `false` even when the form carries a `true`, which is what a chart saved while its type still
 * had the checkbox reopens with. Within a type that does draw one, the user's answer is taken verbatim,
 * and `undefined` — the checkbox never rendered — means the type's own default.
 */
export function legendVisibleFromForm(chartType: WorkspaceChartTypeEnum, showLegend: boolean | undefined): boolean {
	return chartTypeDrawsLegend(chartType) && (showLegend ?? true);
}

/**
 * The key-metric threshold's own concept, when the form's mode says there is one.
 *
 * `chart.relativeThreshold` is a *reference*, and this used to be a `createParametrizedConcept` call made
 * before the chart was written — twice, once per panel, field for field the same. It is now one function
 * whose result is part of the `addChart` / `editChart` payload, so the reference resolves by construction
 * instead of by the two calls happening in the right order.
 *
 * `dataProvider` is carried, unlike on every other stored concept: here it *is* the threshold's mode
 * (Portfolio / Reference), and it is the only record of it — which is what lets the panel reopen showing
 * the mode the user chose.
 */
/** @internal — exported for tests */
export function thresholdConceptFromForm(
	values: WorkspaceChartSettingsDetails,
	chartType: WorkspaceChartTypeEnum,
): ParameterizedConceptDTO | undefined {
	const dataProvider = values.dataProvider;
	if (
		!chartTypeSupportsThreshold(chartType) ||
		(dataProvider?.dataProviderId !== "investment" && dataProvider?.dataProviderId !== "reference") ||
		dataProvider?.parameters?.uuid == null
	) {
		return undefined;
	}

	return {
		conceptId: values.concept!.id!,
		label: values.name!,
		representationMode: values.representationMode,
		relativeness: values.parameterizedRelativeness,
		timeHorizon: values.timeHorizon!,
		source: "CURRENT",
		confidenceLevel: values.confidenceLevel,
		parameters: values.parameters as ParameterizedConceptDTO["parameters"],
		dataProvider,
	};
}

/**
 * What the chart panels demand at submit time beyond what a chain does: a chart is stored under a label of
 * its own, so it has to have one.
 *
 * Keyed on the model, like every other tightening in the area: the resolver below can constrain a field the
 * model declares but can no longer *introduce* one, which is the property that used to be missing. An
 * undeclared `showLegend` reached submit as `undefined` and got the `?? true` fallback — the value never left
 * the form, the type insisted it had, and `description` was dropped the same way.
 */
const chartPanelSubmitShape = {
	chartName: z.string(),
} satisfies Partial<Record<keyof WorkspaceChartSettingsDetails, unknown>>;

/**
 * The panel's model, submitted: the chain's rules, the chart's own, and then the per-chart-type union.
 *
 * The union is *validation only*. It says which chart types accept which settings — a stacked column with no
 * breakdown to stack is the form's one impossible state — while what the submit handler receives is the whole
 * model either way, because the model is the left-hand side of the intersection. It used to be the union and
 * two hand-written objects that decided that, and a field none of the three happened to name was deleted on
 * its way to the handler.
 */
export const chartSettingsResolverSchema = conceptFormSubmitSchema(
	chartSettingsFormModel.extend(chartPanelSubmitShape),
).and(
	z.discriminatedUnion("chartType", [
		z.object({
			chartType: z.literal("HISTOGRAM"),
			...keyMetricThresholdSchemaShape,
		}),
		z.object({
			chartType: z.literal("STACKED_COLUMN"),
			// A stacked column has nothing to stack without a breakdown. The picker cannot offer the kind
			// without one (see `supportedChartTypes`), so this only catches a form that got there some other
			// way — a persisted chart whose concept lost its breakdown, say.
			breakdown: z.object({ dimension: z.string().min(1) }, { message: "Break a level down with “All” first" }),
			breakdownLimit: z.number().int().positive().nullable().optional(),
			breakdownOthersBucket: z.boolean().optional(),
		}),
		z.object({
			chartType: z.literal("FREQUENCY"),
			// Optional rather than required: an absent mode means AUTO everywhere downstream, and making it
			// required would let a chart switched from `HISTOGRAM` fail validation with nothing on screen to fix.
			binningMode: z.nativeEnum(DistributionBinningModeEnum).optional(),
			binCount: z.number().int().positive().nullable().optional(),
			binWidth: z.number().positive().nullable().optional(),
			...keyMetricThresholdSchemaShape,
		}),
		// The frequency chart's fields under the same rules — the density chart is its own bins read as a
		// density, so binning means the same thing and switching between the two must not invalidate the
		// form — plus the two overlays only this kind draws.
		z.object({
			chartType: z.literal("DENSITY"),
			binningMode: z.nativeEnum(DistributionBinningModeEnum).optional(),
			binCount: z.number().int().positive().nullable().optional(),
			binWidth: z.number().positive().nullable().optional(),
			showMeanLines: z.boolean().optional(),
			showNormalFit: z.boolean().optional(),
			...keyMetricThresholdSchemaShape,
		}),
		// A line chart has no settings of its own: the time horizon is the chain's, the legend is shared
		// with the other two, and there is nothing to bin or to limit.
		z.object({ chartType: z.literal("PERFORMANCE_LINE") }),
	]),
);

/**
 * The chart-type-specific property bags, as persisted.
 *
 * `breakdownProperties` / `distributionProperties` are optional siblings rather than a `oneOf`
 * discriminated on `chartType` — that matches how `histogramProperties` already sits on the DTO, and the
 * frontend validates properly via zod anyway. Only the bag for the selected kind is written, so
 * switching a chart's type does not leave the previous type's settings behind to be silently reused.
 *
 * `breakdownProperties.dimension` is copied out of the chain rather than read from a control of its
 * own — the "All" leaf is the only thing that writes it.
 */
/** @internal — exported for tests */
export function chartTypePropertiesFromForm(
	values: WorkspaceChartSettingsDetails,
): Pick<SmartWorkspaceDTOChart, "breakdownProperties" | "distributionProperties"> {
	const chartType = values.chartType ?? "HISTOGRAM";
	return {
		breakdownProperties:
			chartType === "STACKED_COLUMN" && values.breakdown
				? {
						dimension: values.breakdown.dimension,
						limit: values.breakdownLimit ?? undefined,
						othersBucket: values.breakdownOthersBucket ?? undefined,
					}
				: undefined,
		distributionProperties:
			chartType === "FREQUENCY" || chartType === "DENSITY"
				? {
						mode: values.binningMode ?? DistributionBinningModeEnum.Auto,
						binCount:
							values.binningMode === DistributionBinningModeEnum.Count ? (values.binCount ?? undefined) : undefined,
						binWidth:
							values.binningMode === DistributionBinningModeEnum.Width ? (values.binWidth ?? undefined) : undefined,
					}
				: undefined,
	};
}

/**
 * The density chart's overlay toggles, as persisted.
 *
 * They live in `preferences` rather than in `distributionProperties` because they change nothing the
 * backend bins — the binning is the query key the frequency and the density charts share, and a chart that
 * only hid its mean lines must not refetch.
 *
 * Written as explicit `undefined` for every other kind, exactly as {@link chartTypePropertiesFromForm}
 * drops the bag a kind does not own: `preferences` reaches the reducer through a deep `patch`, where an
 * absent key means "leave it alone" and a present `undefined` clears it. So a density chart edited into a
 * column does not keep answers nobody can see, ready to be reused if it is edited back.
 */
/** @internal — exported for tests */
export function chartPreferencesFromForm(
	values: WorkspaceChartSettingsDetails,
): Pick<WorkspaceChartCtx, "preferences"> {
	const isDensity = (values.chartType ?? "HISTOGRAM") === "DENSITY";
	return {
		preferences: {
			showMeanLines: isDensity ? (values.showMeanLines ?? true) : undefined,
			showNormalFit: isDensity ? (values.showNormalFit ?? true) : undefined,
		},
	};
}

const binningModeOptions = [
	{ label: "Automatic", value: DistributionBinningModeEnum.Auto },
	{ label: "Number of bins", value: DistributionBinningModeEnum.Count },
	{ label: "Bin width", value: DistributionBinningModeEnum.Width },
];

/**
 * The step *after* the chain in both panels: how to draw the data the chain just described.
 *
 * Deliberately not step 0. What can be drawn follows from what was picked (FR-1.2), so asking first
 * would mean offering all three and then silently rewriting the answer — the user picks *what* they
 * want to see, then *how*. With no metric yet there is nothing to draw, so the field is not rendered.
 *
 * Kinds that cannot draw the current chain are left out of the list rather than shown disabled, since
 * inside a closed dropdown a greyed-out row explains nothing until the list is opened.
 *
 * A breakdown gets a hint saying so, because it is the narrowing that surprises: asking for one is an
 * act the user just performed, and it collapses the list to the stacked column alone. The other
 * direction needs no hint — with nothing broken down the stacked column is missing because it has
 * nothing to stack, which is not a state anyone arrives at by accident. Note that the two conditions
 * are mutually exclusive, so all five kinds are never on offer together and "the full list" is not a
 * state to write copy for.
 */
/** @internal — exported for tests */
export function ChartTypeFormPartial({ form }: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails> }>) {
	const concept = form.watch("concept");
	const breakdown = form.watch("breakdown");
	const chartType = form.watch("chartType") ?? "HISTOGRAM";
	const available = useMemo<ReadonlyArray<WorkspaceChartTypeEnum>>(() => {
		const supported = supportedChartTypes(concept, { breakdown });
		// A metric that supports neither new kind is still a column chart — that is what it was before
		// this picker existed — so `HISTOGRAM` is the floor, rather than falling back to offering everything.
		return supported.length > 0 ? supported : ["HISTOGRAM"];
	}, [concept, breakdown]);

	// Runs whether or not the field is on screen, so switching to a metric that cannot do the current
	// kind corrects the form even while the picker is still hidden.
	useEffect(() => {
		if (!available.includes(chartType)) {
			form.setValue("chartType", available[0]);
		}
	}, [available, chartType, form]);

	if (!concept) {
		return null;
	}

	return (
		<FormField
			label="Chart type"
			hint={
				breakdown != null
					? "Breaking a level down gives one value per category, which only the stacked column can draw."
					: undefined
			}
		>
			{(forward) => (
				<Select<WorkspaceChartTypeEnum>
					{...forward}
					className="mt-1"
					size="small"
					triggerDataAttrs={{ "data-qualifier": "SelectChartType" }}
					value={chartType}
					options={available.map((chartType) => ({
						value: chartType,
						label: workspaceChartTypeLabels[chartType],
						icon: workspaceChartTypeIcons[chartType],
						"data-qualifier": `SelectChartType/${chartType}`,
					}))}
					// `available` is never empty — `HISTOGRAM` is its floor — so a cleared value falls back to
					// a kind that can draw rather than to none.
					onChange={(value) => form.setValue("chartType", value ?? available[0])}
				/>
			)}
		</FormField>
	);
}

/**
 * The type-conditional part of the panel: the column chart gets the threshold, STACKED gets break-down-by +
 * top-N/Others + legend, FREQUENCY gets the binning mode + the threshold, DENSITY those two plus its overlay
 * toggles and the legend, LINE gets the legend alone.
 *
 * The threshold partial is reused verbatim on the two distribution kinds (FR-8) rather than reduced to a
 * manual-only variant: it is the same setting on the same metric, so all three modes — Manual, Portfolio,
 * Reference — mean exactly what they mean on the column chart. Only the *axis* it lands on differs, which
 * is the chart's business and not the form's. {@link chartTypeSupportsThreshold} is the rule.
 *
 * The legend checkbox is rendered by exactly the kinds that draw a legend, and is on by default there —
 * see {@link ShowLegendFormPartial} and `chartTypeDrawsLegend`. The other two hardcode
 * `legend: {enabled: false}`, so their checkbox stays hidden rather than lying.
 *
 * A panel carries {@link displayOptionsLabel} at most once, so wherever the legend follows a section that
 * already has it — the stacked column's "Other" bucket, the density chart's overlays — it is rendered
 * uncaptioned inside that section instead. The line chart is the one kind whose legend opens the section
 * itself, and there it keeps the caption.
 */
function ChartTypeConditionalFields({
	form,
	chartType,
}: {
	form: UseFormReturn<WorkspaceChartSettingsDetails>;
	chartType: WorkspaceChartTypeEnum;
}) {
	return (
		<Switch
			case={chartType}
			match={{
				HISTOGRAM: () => <KeyMetricThresholdFormPartial form={form} />,
				STACKED_COLUMN: () => (
					// One display-options section: the legend joins the "Other" bucket under the caption
					// `BreakdownFormPartial` already carries, rather than opening a second one under the same words.
					<Column gap={8} flexGrow={0}>
						<BreakdownFormPartial form={form} />
						<ShowLegendFormPartial form={form} captioned={false} />
					</Column>
				),
				FREQUENCY: () => (
					<Column gap={16} flexGrow={0}>
						<BinningFormPartial form={form} />
						<KeyMetricThresholdFormPartial form={form} />
					</Column>
				),
				DENSITY: () => (
					<Column gap={16} flexGrow={0}>
						<BinningFormPartial form={form} />
						<KeyMetricThresholdFormPartial form={form} />
						{/* One display-options section, not two: the overlays and the legend are three answers to the
						    same question, so they sit under one caption at one row spacing. */}
						<Column gap={8} flexGrow={0}>
							<DensityOverlaysFormPartial form={form} />
							<ShowLegendFormPartial form={form} captioned={false} />
						</Column>
					</Column>
				),
				PERFORMANCE_LINE: () => <ShowLegendFormPartial form={form} />,
			}}
		/>
	);
}

/**
 * The caption every section of display checkboxes carries. Plural whether it heads one checkbox or three:
 * it names the section, not the controls under it. One constant so three panels cannot drift into three
 * spellings of the same heading.
 */
const displayOptionsLabel = "Display options";

/**
 * On by default, off only if the user says so: this control is rendered exactly by the kinds that draw a
 * legend, so reaching it with no value yet means the kind's own default applies — and the checkbox has to
 * show that default rather than start unchecked against a chart that will render the legend anyway.
 *
 * An explicit `false` is left alone, which is why the seed is `undefined` rather than `false` in the
 * panels' `defaultValues`: the two are only distinguishable when "not answered" has its own value.
 *
 * `captioned` is how it joins a section already headed by {@link displayOptionsLabel} — the density panel,
 * where the legend is one display option among three — rather than opening a second one under the same
 * heading.
 */
/** @internal — exported for tests */
export function ShowLegendFormPartial({
	form,
	captioned = true,
}: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails>; captioned?: boolean }>) {
	useEffect(() => {
		if (form.getValues("showLegend") === undefined) {
			form.setValue("showLegend", true);
		}
	}, [form]);

	return (
		<FormFields.Checkbox
			name="showLegend"
			control={form.control}
			formState={form.formState}
			label={captioned ? displayOptionsLabel : undefined}
		>
			Show legend
		</FormFields.Checkbox>
	);
}

/**
 * How the breakdown's categories are displayed: top-N + an "Other" bucket.
 *
 * *Which* dimension they come from is not asked here — the chain answered that when the user picked
 * "All" on a level. The label names it back so the two halves read as one sentence.
 */
/** @internal — exported for tests */
export function BreakdownFormPartial({ form }: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails> }>) {
	const concept = form.watch("concept");
	const dimension = form.watch("breakdown")?.dimension;
	// The backend folds a tail into "Other" only when a `limit` truncates something, so with no top-N
	// the checkbox would promise a bucket that never appears.
	const hasLimit = (form.watch("breakdownLimit") ?? 0) > 0;

	// Clearing the top-N clears the flag too. Left checked-but-disabled it would persist a request the
	// backend ignores, and stepping a limit back in would silently resurrect a choice the user last saw
	// greyed out. Runs on seed as well, so a chart persisted in that state is corrected on open.
	useEffect(() => {
		if (!hasLimit && form.getValues("breakdownOthersBucket")) {
			form.setValue("breakdownOthersBucket", false, { shouldDirty: true });
		}
	}, [hasLimit, form]);

	return (
		<Column gap={8} flexGrow={0}>
			{/*
			 * Clearable rather than bottoming out at its minimum: an empty field is a real state — no `limit`
			 * on the wire means every category — and it is the state a new stacked chart opens in.
			 */}
			<FormFields.Stepper
				name="breakdownLimit"
				control={form.control}
				formState={form.formState}
				label={
					dimension ? `Top ${breakdownDimensionLabel(dimension, concept).toLowerCase()} categories` : "Top categories"
				}
				min={1}
				clearable
			/>
			<FormFields.Checkbox
				name="breakdownOthersBucket"
				control={form.control}
				formState={form.formState}
				label={displayOptionsLabel}
				disabled={!hasLimit}
			>
				Group the remaining categories as “Other”
			</FormFields.Checkbox>
		</Column>
	);
}

/**
 * The density chart's two overlays: the per-series mean lines, and the fitted-normal reference curves.
 *
 * Two checkboxes rather than one, and in this order, because they answer two questions — where each group
 * sits, and how far its shape is from a normal of the same μ and σ. The fit is *computed* from the mean, so
 * one control for both is a tempting simplification and the wrong one: a reader comparing two means does
 * not want four curves on the plot, and a reader judging skew does not need the means to do it.
 *
 * Both default on: the chart drew both before there was anything to ask, and an absent preference has to
 * keep meaning that — see the note on `chartPreferenceContent`.
 */
/** @internal — exported for tests */
export function DensityOverlaysFormPartial({ form }: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails> }>) {
	return (
		<Column gap={8} flexGrow={0}>
			<FormFields.Checkbox
				name="showMeanLines"
				control={form.control}
				formState={form.formState}
				label={displayOptionsLabel}
			>
				Show mean lines
			</FormFields.Checkbox>
			<FormFields.Checkbox name="showNormalFit" control={form.control} formState={form.formState}>
				Show normal fit
			</FormFields.Checkbox>
		</Column>
	);
}

/**
 * How the chart is named and described: the name, the checkbox that generates it, and the free
 * description. Both panels end with these three, and the description is a sibling of the name column
 * rather than inside it — hence the fragment, which leaves the panel's own 16px between the two.
 *
 * Ticking Auto-generated regenerates `name` *and* `chartName`: the chain owns the concept's name and the
 * chart its own label, and this is the one control that writes both.
 */
function ChartNamingFormPartial({ form }: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails> }>) {
	return (
		<>
			<Column gap={8} flexGrow={0}>
				<FormFields.Text
					name="chartName"
					control={form.control}
					formState={form.formState}
					label="Name"
					disabled={form.watch("autoGenerateName")}
				/>
				<FormFields.Checkbox
					name="autoGenerateName"
					control={form.control}
					formState={form.formState}
					onChange={(shouldAutogenerate) => {
						if (shouldAutogenerate) {
							form.reset({
								...form.getValues(),
								name: autoGenerateMetricName(form.getValues(), { ignoreSource: true }),
								chartName: autoGenerateMetricName(form.getValues(), { ignoreSource: true }),
							});
						}
					}}
				>
					Auto-generated
				</FormFields.Checkbox>
			</Column>
			<FormFields.TextArea
				name="description"
				control={form.control}
				formState={form.formState}
				label="Description"
				placeholder="Add a free description to this item. The text will be displayed in the list"
			/>
		</>
	);
}

/** Binning mode + its parameter. Shared by `FREQUENCY` and `DENSITY`. */
/** @internal — exported for tests */
export function BinningFormPartial({ form }: Readonly<{ form: UseFormReturn<WorkspaceChartSettingsDetails> }>) {
	const mode = form.watch("binningMode") ?? DistributionBinningModeEnum.Auto;
	// A bin width is a span of the metric, so it carries the metric's unit — same as the manual
	// threshold, and same as the axis labels the bins end up under. A bin *count* is a plain count,
	// so it stays unitless.
	const unit = form.watch("concept.unitOfMeasurement");

	return (
		<Column gap={4} flexGrow={0}>
			<div className="grid grid-cols-2 gap-4 items-end">
				<FormField label="Binning">
					{(forward) => (
						<Select
							{...forward}
							triggerDataAttrs={{ "data-qualifier": "SelectBinningMode" }}
							value={mode}
							options={binningModeOptions}
							onChange={(value) => form.setValue("binningMode", value ?? DistributionBinningModeEnum.Auto)}
						/>
					)}
				</FormField>
				<Switch
					case={mode}
					match={{
						AUTO: () => null,
						COUNT: () => (
							<FormFields.NullableNumber
								name="binCount"
								control={form.control}
								formState={form.formState}
								label={<>&nbsp;</>}
								min={1}
							/>
						),
						WIDTH: () =>
							unit === "%" ? (
								<FormFields.Percentage
									name="binWidth"
									control={form.control}
									formState={form.formState}
									label={<>&nbsp;</>}
									min={0}
								/>
							) : (
								<FormFields.NullableNumber
									name="binWidth"
									control={form.control}
									formState={form.formState}
									label={<>&nbsp;</>}
									min={0}
									rightContent={unit || undefined}
								/>
							),
					}}
				/>
			</div>
		</Column>
	);
}

type WorkspaceEditChartSettingsProps = {
	index: number;
	title: string;
	description?: string;
	initialData?: Partial<WorkspaceChartSettingsDetails>;
};

export function WorkspaceEditChartSettingsStack(props: WorkspaceEditChartSettingsProps) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const dispatch = useWorkspaceDispatch();

	// `name` and `chartName` are both REQUIRED by the resolver, and both are seeded from the
	// persisted chart `label` — which the DTO types as optional and the Sphere AI workspace tools
	// can leave unset. Without this fallback such a chart opens with an unsatisfiable form: submit
	// fails validation silently, so "Update" does nothing and the chart can never be edited. Match
	// the placeholder the list and the chart header already render for a label-less chart.
	const chartName = props.initialData?.chartName ?? DEFAULT_CHART_LABEL;
	const form = useForm<WorkspaceChartSettingsDetails>({
		defaultValues: {
			autoGenerateName: true,
			...(props.initialData ?? {}),
			chartName,
			name: chartName,
			// After the spread, so an `initialData` that carries these keys as explicit `undefined` (which a
			// column chart always does) cannot clobber the fallbacks.
			chartType: props.initialData?.chartType ?? "HISTOGRAM",
			binningMode: props.initialData?.binningMode ?? DistributionBinningModeEnum.Auto,
		},
		resolver: zodResolver(chartSettingsResolverSchema),
	});
	const autoGenerateName = form.watch("autoGenerateName");
	const chartType = form.watch("chartType") ?? "HISTOGRAM";

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={{
					label: "Update",
					async onClickAsync() {
						await handleSubmitToCustomSubmitHandler({
							handleSubmit: form.handleSubmit,
							baseSubmitFn: (workspaceConcept) => {
								if (!workspaceConcept.concept?.id) {
									console.error("missing concept id");
									return;
								}

								const chartType = workspaceConcept.chartType ?? "HISTOGRAM";
								// One series per source the chosen kind's form allows — see `chartSeriesConcepts`. The
								// form's `dataProvider` is deliberately not passed on: in this form it is the *threshold*
								// mode, not a property of the charted metric, and it is consumed below.
								const parameterizedConcepts = chartSeriesConcepts(workspaceConcept.concept, chartType, {
									label: workspaceConcept.name!,
									timeHorizon: workspaceConcept.timeHorizon,
									confidenceLevel: workspaceConcept.confidenceLevel,
									representationMode: workspaceConcept.representationMode,
									relativeness: workspaceConcept.parameterizedRelativeness,
									parameters: workspaceConcept.parameters as ParameterizedConceptDTO["parameters"],
								});

								if (parameterizedConcepts.length === 0) {
									throw new Error("missing allowed source cause to miss parametrized concepts");
								}

								// The threshold concept travels *with* the operation instead of being minted by a separate
								// call first: `relativeThreshold` is a reference, and the gap between the two calls was a
								// window in which the chart pointed at a concept the pool did not hold yet.
								const thresholdConcept = thresholdConceptFromForm(workspaceConcept, chartType);
								void dispatch({
									type: "editChart",
									index: props.index,
									concepts: parameterizedConcepts,
									thresholdConcept,
									chart: {
										chartLegend: {
											visible: legendVisibleFromForm(chartType, workspaceConcept.showLegend),
										},
										chartType,
										...chartTypePropertiesFromForm(workspaceConcept),
										...chartPreferencesFromForm(workspaceConcept),
										label: workspaceConcept.chartName,
										description: workspaceConcept.description,
										// Persisted for every chart type that has an axis to draw it on, not for the column chart
										// alone (FR-8) — `chartTypeSupportsThreshold` is the single rule, so a type that grows or
										// loses the setting changes one function rather than four literals.
										manualThreshold:
											!chartTypeSupportsThreshold(chartType) || thresholdConcept
												? undefined
												: (workspaceConcept.manualThreshold ?? undefined),
										// Cleared when the form says Manual, which is what makes reopening the panel honest.
										...(thresholdConcept ? {} : { relativeThreshold: undefined }),
									},
								});
								sidePanelCtx.pop();
							},
						})();
					},
				}}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			{/* The form is taller than the panel as soon as a type with its own settings is picked, so the
			    content scrolls rather than being clipped between the header and the footer. */}
			<SidePanelContent className="min-h-0 flex-1 overflow-y-auto">
				<Column gap={16}>
					<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
						<WorkspaceChartChain
							allowedConcepts="METRICS_ONLY"
							chartType={chartType}
							initialValue={form.watch()}
							autogenerateName={autoGenerateName}
							onChange={(newWorkspaceChain) => {
								form.reset({
									...newWorkspaceChain,
									chartName: newWorkspaceChain.name,
									autoGenerateName: form.getValues("autoGenerateName"),
									showLegend: form.getValues("showLegend"),
									description: form.getValues("description"),
									chartType: form.getValues("chartType"),
									breakdownLimit: form.getValues("breakdownLimit"),
									breakdownOthersBucket: form.getValues("breakdownOthersBucket"),
									binningMode: form.getValues("binningMode"),
									binCount: form.getValues("binCount"),
									binWidth: form.getValues("binWidth"),
									showMeanLines: form.getValues("showMeanLines"),
									showNormalFit: form.getValues("showNormalFit"),
								});
							}}
							hideConceptSourceSelection
							allowBreakdown
						/>
					</div>
					<ChartTypeFormPartial form={form} />
					<ChartTypeConditionalFields form={form} chartType={chartType} />
					<Divider />
					<ChartNamingFormPartial form={form} />
				</Column>
			</SidePanelContent>
			<SidePanelFooter
				className={"z-10"}
				neutralAction={() => (
					<Button
						palette="secondary"
						size="small"
						className="gap-1"
						onClick={() => {
							sidePanelCtx.pop();
							void dispatch({ type: "removeChart", index: props.index });
						}}
						data-qualifier={qualifier.component.button("Delete")}
					>
						<Icon icon="Delete" size={16} /> Delete
					</Button>
				)}
			/>
		</>
	);
}

/** Create and edit share one form model; the alias is kept for the existing imports. */
export type WorkspaceCreateChartSettingsForm = WorkspaceChartSettingsDetails;

function KeyMetricThresholdFormPartial({ form }: { form: UseFormReturn<WorkspaceCreateChartSettingsForm> }) {
	const dataType = form.watch("concept.dataType");
	const sanitizedThresholdModeOptions = useMemo(() => {
		if (dataType && isCustomAttributeConcept(dataType)) {
			return thresholdModeOptions.filter((x) => x.value === "investment" || x.value === "manual");
		}
		return thresholdModeOptions;
	}, [dataType]);
	return (
		<div className="grid grid-cols-2 gap-4 items-end">
			<FormField label="Key Metric Threshold">
				{(forward) => (
					<Select
						{...forward}
						value={form.watch("dataProvider")?.dataProviderId ?? "manual"}
						onChange={(value: (typeof thresholdModeOptions)[number]["value"] | null) => {
							switch (value) {
								case null:
								case "manual":
									form.setValue("dataProvider", null);
									break;
								case "reference":
								case "investment":
									form.setValue("dataProvider", {
										dataProviderId: value,
										parameters: { uuid: null as unknown as object },
									});
									form.setValue("manualThreshold", null);
									break;
								default:
									unreachable(value);
							}
						}}
						options={sanitizedThresholdModeOptions}
					/>
				)}
			</FormField>

			<Switch
				case={
					(form.watch("dataProvider.dataProviderId") as (typeof thresholdModeOptions)[number]["value"] | null) ??
					"manual"
				}
				match={{
					manual: () =>
						form.watch("concept.unitOfMeasurement") === "%" ? (
							<FormFields.Percentage
								name="manualThreshold"
								control={form.control}
								formState={form.formState}
								label={<>&nbsp;</>}
								min={-100}
							/>
						) : (
							<FormFields.NullableNumber
								name="manualThreshold"
								control={form.control}
								formState={form.formState}
								label={<>&nbsp;</>}
							/>
						),
					investment: () => (
						<ReactQueryWrapper
							{...workspaceRepository.getAllPortfolios.getQueryConfig()}
							loadingFallback={<Select value={null} options={[]} disabled />}
						>
							{(data) => (
								<FormFields.Select
									name="dataProvider.parameters.uuid"
									control={form.control}
									formState={form.formState}
									label={<>&nbsp;</>}
									options={
										data.items?.map((item) => ({
											label: item.predefinedFields?.name ?? "",
											value: item.predefinedFields?.uuid ?? "",
										})) ?? []
									}
								/>
							)}
						</ReactQueryWrapper>
					),
					reference: () => (
						<ReactQueryWrapper
							{...workspaceRepository.getAllReferences.getQueryConfig()}
							loadingFallback={<Select value={null} options={[]} disabled />}
						>
							{(data) => (
								<FormFields.Select
									name="dataProvider.parameters.uuid"
									control={form.control}
									formState={form.formState}
									label={<>&nbsp;</>}
									options={
										data.items?.map((item) => ({
											label: item.predefinedFields?.name ?? "",
											value: item.predefinedFields?.uuid ?? "",
											group: investmentTypeToGroupLabel[item.predefinedFields?.investmentType ?? "INVESTMENT"],
										})) ?? []
									}
								/>
							)}
						</ReactQueryWrapper>
					),
				}}
			/>
		</div>
	);
}

export function WorkspaceCreateChartSettingsStack(props: { title: string; description?: string }) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const form = useForm<WorkspaceCreateChartSettingsForm>({
		defaultValues: {
			autoGenerateName: true,
			chartName: DEFAULT_CHART_LABEL,
			chartType: "HISTOGRAM",
			binningMode: DistributionBinningModeEnum.Auto,
			// On unless the user says otherwise, and a checkbox renders `undefined` as unchecked — so the
			// default is seeded here rather than left to the write side.
			showMeanLines: true,
			showNormalFit: true,
		},
		resolver: zodResolver(chartSettingsResolverSchema),
	});

	const autoGenerateName = form.watch("autoGenerateName");
	const chartType = form.watch("chartType") ?? "HISTOGRAM";
	const dispatch = useWorkspaceDispatch();

	return (
		<>
			<SidePanelHeader
				title={props.title}
				description={props.description}
				icon={{
					name: "Left",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
				primaryAction={{
					label: "Apply",
					async onClickAsync() {
						await handleSubmitToCustomSubmitHandler({
							handleSubmit: form.handleSubmit,
							baseSubmitFn: (parametrizedConcept) => {
								if (!parametrizedConcept.concept?.id) {
									throw new Error("missing concept id");
								}

								const chartType = parametrizedConcept.chartType ?? "HISTOGRAM";
								// See the sibling edit panel: the series are the sources the chosen kind's form allows,
								// which for a line are its history's and not the scalar's.
								const parameterizedConcepts = chartSeriesConcepts(parametrizedConcept.concept, chartType, {
									label: parametrizedConcept.name!,
									timeHorizon: parametrizedConcept.timeHorizon,
									confidenceLevel: parametrizedConcept.confidenceLevel,
									representationMode: parametrizedConcept.representationMode,
									relativeness: parametrizedConcept.parameterizedRelativeness,
									parameters: parametrizedConcept.parameters as ParameterizedConceptDTO["parameters"],
								});

								if (parameterizedConcepts.length === 0) {
									throw new Error("missing allowed source cause to miss parametrized concepts");
								}

								// See the sibling edit panel: one dispatch carries the chart and its threshold concept.
								const thresholdConcept = thresholdConceptFromForm(parametrizedConcept, chartType);
								void dispatch({
									type: "addChart",
									concepts: parameterizedConcepts,
									thresholdConcept,
									chart: {
										chartLegend: {
											visible: legendVisibleFromForm(chartType, parametrizedConcept.showLegend),
										},
										chartType,
										...chartTypePropertiesFromForm(parametrizedConcept),
										...chartPreferencesFromForm(parametrizedConcept),
										label: parametrizedConcept.chartName,
										description: parametrizedConcept.description,
										// See the sibling edit panel: the threshold follows `chartTypeSupportsThreshold`, which
										// covers the two distribution types as well as the column chart (FR-8).
										manualThreshold:
											!chartTypeSupportsThreshold(chartType) || thresholdConcept
												? undefined
												: (parametrizedConcept.manualThreshold ?? undefined),
										relativeThreshold: undefined,
									},
								});
								sidePanelCtx.pop();
							},
						})();
					},
				}}
				secondaryAction={{
					label: "Cancel",
					onClick() {
						sidePanelCtx.pop();
					},
				}}
			/>
			<Divider />
			{/* Same as the edit panel: the chain plus the type-specific settings outgrow the panel, so the
			    content scrolls instead of running off the bottom. */}
			<SidePanelContent className="min-h-0 flex-1 overflow-y-auto">
				<Column gap={16}>
					<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
						<WorkspaceChartChain
							allowedConcepts="METRICS_ONLY"
							chartType={chartType}
							autogenerateName={autoGenerateName}
							onChange={(newWorkspaceChain) => {
								form.reset({
									...newWorkspaceChain,
									chartName: newWorkspaceChain.name,
									autoGenerateName: form.getValues("autoGenerateName"),
									showLegend: form.getValues("showLegend"),
									description: form.getValues("description"),
									chartType: form.getValues("chartType"),
									breakdownLimit: form.getValues("breakdownLimit"),
									breakdownOthersBucket: form.getValues("breakdownOthersBucket"),
									binningMode: form.getValues("binningMode"),
									binCount: form.getValues("binCount"),
									binWidth: form.getValues("binWidth"),
									showMeanLines: form.getValues("showMeanLines"),
									showNormalFit: form.getValues("showNormalFit"),
								});
							}}
							hideConceptSourceSelection
							allowBreakdown
						/>
					</div>
					<ChartTypeFormPartial form={form} />
					<ChartTypeConditionalFields form={form} chartType={chartType} />
					<Divider />
					<ChartNamingFormPartial form={form} />
				</Column>
			</SidePanelContent>
		</>
	);
}

const thresholdModeOptions = [
	{
		label: "Manual",
		value: "manual" as const,
	},
	{
		label: "Portfolio",
		value: "investment" as const,
	},
	{
		label: "Reference",
		value: "reference" as const,
	},
];

const investmentTypeToGroupLabel: Record<MandatoryItemDataInvestmentTypeEnum, string> = {
	INVESTMENT: "Portfolio",
	REFERENCE: "Reference",
	BENCHMARK: "Benchmark",
};
